import { useMemo, useState } from "react";
import { Languages, Loader2, RotateCcw } from "lucide-react";
import { translateText } from "../lib/api";

/**
 * TranslateButton — inline "🌐 Translate" affordance for any surface
 * that renders user-submitted text (research queue, inference queue,
 * console, submissions, forum, etc.).
 *
 * Behaviour:
 *   1. Auto-detects non-English text via a Unicode heuristic. The
 *      button surfaces itself ONLY when the text looks non-English
 *      (or when `alwaysShow` is set).
 *   2. First click POSTs to /api/translate. Renders the English
 *      translation inline, plus a "Show original" toggle.
 *   3. Server-side cache means repeat clicks are instant/free.
 *
 * Props:
 *   text            (string, required) — the source text to translate.
 *   className       (string) — outer wrapper class.
 *   inline          (bool, default false) — inline pill vs. block button.
 *   alwaysShow      (bool, default false) — surface even for English.
 *   testIdPrefix    (string, default "translate") — for data-testid.
 *   onTranslated    (fn) — callback(result) after successful translate.
 *   renderTranslation (fn) — custom renderer(translation, source_lang).
 */
export function TranslateButton({
  text,
  className = "",
  inline = false,
  alwaysShow = false,
  testIdPrefix = "translate",
  onTranslated,
  renderTranslation,
}) {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [error, setError] = useState("");

  const needsTranslation = useMemo(
    () => alwaysShow || looksNonEnglish(text || ""),
    [text, alwaysShow]
  );

  if (!text || !text.trim()) return null;
  if (!needsTranslation && state === "idle") return null;

  const handleTranslate = async () => {
    if (state === "loading") return;
    setState("loading");
    setError("");
    try {
      const data = await translateText(text, "en");
      setResult(data);
      setState("done");
      onTranslated?.(data);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Translation failed. Try again.";
      setError(String(msg));
      setState("error");
    }
  };

  const langLabel = result?.source_lang_name || result?.source_lang || "";
  const isAlreadyEnglish = result?.already_target;

  return (
    <div
      className={`translate-btn-wrap ${inline ? "inline-flex" : "block"} ${className}`}
      data-testid={`${testIdPrefix}-wrap`}
    >
      {state !== "done" && (
        <button
          type="button"
          onClick={handleTranslate}
          disabled={state === "loading"}
          data-testid={`${testIdPrefix}-btn`}
          className={
            inline
              ? "inline-flex items-center gap-1.5 rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2.5 py-0.5 text-[11px] font-medium text-cyan-200 hover:bg-cyan-500/20 hover:border-cyan-400/60 transition-colors disabled:opacity-60"
              : "inline-flex items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 text-xs font-medium text-cyan-200 hover:bg-cyan-500/20 hover:border-cyan-400/60 transition-colors disabled:opacity-60"
          }
          title="Translate to English"
        >
          {state === "loading" ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Languages className="h-3 w-3" />
          )}
          <span>
            {state === "loading" ? "Translating…" : "Translate"}
          </span>
        </button>
      )}

      {state === "error" && (
        <div
          className="mt-1 text-[11px] text-red-300"
          data-testid={`${testIdPrefix}-error`}
        >
          {error}
        </div>
      )}

      {state === "done" && result && (
        <div
          className="mt-2 rounded-md border border-cyan-500/30 bg-cyan-950/30 px-3 py-2 text-xs"
          data-testid={`${testIdPrefix}-result`}
        >
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-300">
              <Languages className="h-3 w-3" />
              {isAlreadyEnglish
                ? "Already English"
                : `Translated from ${langLabel || "auto-detected"} → English`}
              {result?.cached && (
                <span className="ml-1 rounded bg-cyan-500/20 px-1 py-0.5 text-[9px] font-normal text-cyan-200">
                  cached
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowOriginal((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] text-cyan-300 hover:text-cyan-100"
              data-testid={`${testIdPrefix}-toggle-original`}
            >
              <RotateCcw className="h-3 w-3" />
              {showOriginal ? "Hide original" : "Show original"}
            </button>
          </div>
          {renderTranslation ? (
            renderTranslation(result.translation, result.source_lang)
          ) : (
            <p
              className="whitespace-pre-wrap text-slate-100"
              data-testid={`${testIdPrefix}-translation`}
            >
              {result.translation}
            </p>
          )}
          {showOriginal && (
            <div
              className="mt-2 border-t border-cyan-500/20 pt-2 text-[11px] text-slate-400"
              data-testid={`${testIdPrefix}-original`}
            >
              <div className="mb-0.5 text-[9px] uppercase tracking-wider text-slate-500">
                Original
              </div>
              <p className="whitespace-pre-wrap">{text}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Heuristic: is this text likely NOT English?
 * Cheap, client-side, no API call. Triggers the button whenever:
 *   - text contains any non-Latin script character (Devanagari, CJK,
 *     Cyrillic, Arabic, Hebrew, Thai, Greek, etc.), OR
 *   - text contains a meaningful density of Latin-with-diacritics
 *     characters (á, ñ, ü, ç, ê, ø, …) suggesting Romance/Germanic
 *     non-English (Spanish, French, Portuguese, German, Italian, …).
 * The false-positive rate is intentional: better to occasionally show
 * "Translate" on a proper noun than to hide it on real Spanish text.
 */
export function looksNonEnglish(text) {
  if (!text) return false;
  const t = String(text);
  // Any non-Latin script → definitely non-English.
  //   Devanagari  U+0900–U+097F   (Hindi/Marathi/Sanskrit)
  //   Bengali     U+0980–U+09FF
  //   Tamil       U+0B80–U+0BFF
  //   CJK         U+3400–U+9FFF, U+F900–U+FAFF, U+3040–U+30FF
  //   Hangul      U+AC00–U+D7AF
  //   Cyrillic    U+0400–U+04FF
  //   Arabic      U+0600–U+06FF
  //   Hebrew      U+0590–U+05FF
  //   Thai        U+0E00–U+0E7F
  //   Greek       U+0370–U+03FF
  if (
    /[\u0900-\u097F\u0980-\u09FF\u0B80-\u0BFF\u3040-\u30FF\u3400-\u9FFF\uAC00-\uD7AF\u0400-\u04FF\u0600-\u06FF\u0590-\u05FF\u0E00-\u0E7F\u0370-\u03FF]/.test(
      t
    )
  ) {
    return true;
  }
  // Diacritic density heuristic — Latin-with-accents.
  const diacritics = t.match(/[\u00C0-\u024F]/g);
  if (diacritics && diacritics.length >= 2) return true;
  if (diacritics && diacritics.length === 1 && t.length < 60) return true;
  // Common non-English stopwords — very cheap catch for short prompts.
  const lower = " " + t.toLowerCase() + " ";
  const stops = [
    " el ", " la ", " los ", " las ", " que ", " está ", " pero ", " para ",
    " porque ", " cómo ", " qué ", " dónde ", " cuál ",
    " le ", " les ", " est ", " être ", " avec ", " pourquoi ", " comment ",
    " parce ", " où ",
    " der ", " die ", " das ", " und ", " ist ", " nicht ", " warum ", " wie ",
    " il ", " lo ", " gli ", " perché ", " come ",
    " não ", " porque ", " onde ",
  ];
  let hits = 0;
  for (const s of stops) if (lower.includes(s)) hits++;
  return hits >= 2;
}

export default TranslateButton;
